import { ReactNode, useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import { Box, Stack, Typography, Button, ButtonProps, PaperProps } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';

import { createSimpleStep, createConditionalStep, createFormStep, createComponentStep } from './helpers/stepHelpers';
import usePersistStepperProgress from './hooks/usePersistStepperProgress';
import useSlideNavigation from './hooks/useSlideNavigation';
import { getDisplayFlowName } from './helpers/trackingNames';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import FullScreenDialog, { FullScreenDialogProps } from 'modules/common/components/MUI/FullScreenDialog';
import FullScreenDialogContent, {
    FullScreenDialogContentProps,
} from 'modules/common/components/MUI/FullScreenDialog/FullScreenDialogContent';
import FullScreenDialogHeader, {
    FullScreenDialogHeaderProps,
} from 'modules/common/components/MUI/FullScreenDialog/FullScreenDialogHeader';
import FullScreenDialogFooter, {
    FullScreenDialogFooterProps,
} from 'modules/common/components/MUI/FullScreenDialog/FullScreenDialogFooter';
import { useFullScreenDialog } from 'modules/common/components/MUI/FullScreenDialog/FullScreenDialogContext';
import RightSidebar from 'modules/common/components/Layout/RightSidebar';
import IMC from 'modules/IMC';
import { RIGHT_DRAWER_WIDTH } from 'modules/common/components/RightDrawer/Drawer';
import { useIMCContext } from 'modules/IMC/common/context';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
/**
 * Configuration for a single slide within a multi-slide step.
 * Slides share the parent step's data and lifecycle — they only control
 * presentation (content, title, button labels) and per-slide validation gating.
 */
export interface SlideConfig<TData = any> {
    /** Slide title (overrides step title when active) */
    title?: string;
    /** Slide description (overrides step description when active) */
    description?: string;
    /** Render prop for slide content — receives the same StepHelpers as the step */
    content: (helpers: StepHelpers<TData>) => ReactNode;
    /** Custom next button label for this slide */
    nextButtonLabel?: string;
    /** Custom previous button label for this slide */
    previousButtonLabel?: string;
    /** Whether canProceed starts as true for this slide (default: false) */
    validateOnMount?: boolean;
    /** Optional custom footer left element for this slide */
    footerLeftElement?: (helpers: StepFooterHelpers<TData>) => ReactNode;
    /** Optional custom footer right element for this slide */
    footerRightElement?: (helpers: StepFooterHelpers<TData>) => ReactNode;
}

// Helper interfaces for type safety
export interface StepHelpers<TData = any> {
    // Validation state
    setCanProceed: (canProceed: boolean) => void;
    canProceed: boolean;

    // Step-specific data management
    setStepData: (data: TData) => void;
    getStepData: () => TData;

    // Navigation callbacks
    onNext?: () => void | Promise<void>;
    /**
     * Completes the wizard from this step, skipping any steps after it — the same exit, completion
     * handler, progress cleanup and tracking as Next on the last step. For a step whose answer can make
     * the steps after it unnecessary.
     */
    onFinish?: () => void | Promise<void>;
    onPrevious?: () => void;
    onSubmit?: (data: TData) => void | Promise<void>;
    goToStep?: (stepNumber: number) => void;

    // Global wizard data
    setWizardData: (key: string, value: any) => void;
    getWizardData: (key: string) => any;
    getAllWizardData: () => Record<string, any>;

    // Step context
    currentStepIndex: number;
    totalSteps: number;
    isFirstStep: boolean;
    isLastStep: boolean;

    // Slide context (populated only when step has slides)
    currentSlideIndex?: number;
    totalSlides?: number;
    isFirstSlide?: boolean;
    isLastSlide?: boolean;
}

export interface StepFooterHelpers<TData = any> extends StepHelpers<TData> {
    canProceed: boolean;
    stepData: TData;
    isLoading?: boolean;
    setIsLoading?: (isLoading: boolean) => void;
}

/**
 * What a render-function sidebar gets from the wizard, so a step navigator can go back the way the
 * footer does without reaching around the wizard's own state.
 */
export interface SidebarHelpers {
    /** True while the wizard is busy (saving a step, completing). The footer is disabled then too. */
    isProcessing: boolean;
    /**
     * Navigates back to a registered step path. Ignored while processing, and tracked as the same
     * "Back Button Clicked" event the footer's Back sends.
     */
    goBackTo: (path: string) => void;
}

export interface StepConfig2<TData = any> {
    /**
     * The route path for this step
     */
    path: string;

    prevPath?: string;
    nextPath?: string;

    /**
     * The title of the step
     */
    title?: string;

    /**
     * Optional header title to override the default wizard title
     */
    headerTitle?: string;

    /**
     * The description of the step
     */
    description?: string;

    /**
     * Render prop for step content with helpers.
     * May return a ReactNode (single-view step) or a SlideConfig array (multi-slide step).
     */
    content: (helpers: StepHelpers<TData>) => ReactNode | SlideConfig<TData>[];

    /**
     * Optional render prop for custom left header element
     */
    headerLeftElement?: (helpers: StepHelpers<TData>) => ReactNode;

    /**
     * Optional render prop for custom right header element
     */
    headerRightElement?: (helpers: StepHelpers<TData>) => ReactNode;

    /**
     * Optional render prop for custom left footer element
     */
    footerLeftElement?: (helpers: StepFooterHelpers<TData>) => ReactNode;

    /**
     * Optional render prop for custom right footer element
     */
    footerRightElement?: (helpers: StepFooterHelpers<TData>) => ReactNode;

    /**
     * Step-specific configuration
     */
    validateOnMount?: boolean;
    initialData?: TData;

    /**
     * Lifecycle hooks
     */
    onStepEnter?: (data: TData, wizardData: Record<string, any>) => void | Promise<void>;
    onStepExit?: (data: TData, wizardData: Record<string, any>) => boolean | Promise<boolean>; // return false to prevent navigation

    /**
     * Custom validation function
     */
    customValidation?: (data: TData, wizardData: Record<string, any>) => boolean | Promise<boolean>;

    /**
     * Hide default footer buttons
     */
    hideDefaultFooter?: boolean;

    /**
     * Hide next button in footer
     */
    hideNextButton?: boolean;

    /**
     * Custom button labels
     */
    nextButtonLabel?: string;
    nextButtonProps?: ButtonProps;
    previousButtonLabel?: string;
    previousButtonProps?: ButtonProps;
    submitButtonLabel?: string;

    /**
     * Target path for "Skip to review" button.
     * When provided, renders a skip button that navigates to this URL.
     */
    skipToReviewPath?: string;

    /**
     * Auto-navigation configuration
     */
    autoNavigate?: {
        nextPath?: string;
        prevPath?: string;
        condition?: (stepData: TData) => boolean;
    };

    /**
     * Optional slot props for the header
     */
    slotProps?: {
        header?: Partial<FullScreenDialogHeaderProps>;
        root?: Partial<FullScreenDialogProps>;
    };

    /**
     * Confirmation modal configuration
     */
    confirmation?: ConfirmationConfig;
}

// New type-based step configuration interfaces
type StepType = 'simple' | 'conditional' | 'form' | 'component';

export interface ConfirmationModalProps {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    stepData?: any;
}

interface ConfirmationConfig {
    component: React.ComponentType<ConfirmationModalProps>;
    condition?: (stepData: any) => boolean;
    onConfirm?: () => void | Promise<void>;
}

interface BaseStepConfig<TData = unknown> {
    type: StepType;
    path: string;
    title?: string;
    headerTitle?: string;
    description?: string;
    prevPath?: string;
    nextButtonLabel?: string;
    prevButtonLabel?: string;
    prevButtonProps?: ButtonProps;
    submitButtonLabel?: string;
    initialData?: TData;
    confirmation?: ConfirmationConfig;
    hideDefaultFooter?: boolean;
    hideNextButton?: boolean;
    slotProps?: {
        header?: Partial<FullScreenDialogHeaderProps>;
    };

    /**
     * Optional render prop for custom left header element
     */
    headerLeftElement?: (helpers: StepHelpers<TData>) => ReactNode;

    /**
     * Optional render prop for custom right header element
     */
    headerRightElement?: (helpers: StepHelpers<TData>) => ReactNode;

    /**
     * Lifecycle hook called before exiting the step
     * Return false to prevent navigation
     */
    onStepExit?: (data: TData, wizardData: Record<string, any>) => boolean | Promise<boolean>;
}

export interface SimpleStepConfig<TData = unknown> extends BaseStepConfig<TData> {
    type: 'simple';
    content: React.ComponentType | ((helpers: StepHelpers<TData>) => ReactNode | SlideConfig<TData>[]);
    nextPath?: string;
    validateOnMount?: boolean;
}

export interface ConditionalStepConfig<TData = unknown> extends BaseStepConfig<TData> {
    type: 'conditional';
    content: (helpers: StepHelpers<TData>) => ReactNode | SlideConfig<TData>[];
    getNextPath: (stepData: TData) => string;
    validateCondition?: (stepData: TData) => boolean;
}

export interface FormStepConfig<TData = unknown> extends BaseStepConfig<TData> {
    type: 'form';
    content: (helpers: StepHelpers<TData>) => ReactNode | SlideConfig<TData>[];
    nextPath?: string;
    validateOnChange?: boolean;
}

export interface ComponentStepConfig<TProps = any, TData = unknown> extends BaseStepConfig<TData> {
    type: 'component';
    component: React.ComponentType<TProps>;
    componentProps?: TProps;
    nextPath?: string;
    validateOnMount?: boolean;
    onPropsChange?: (props: TProps, helpers: StepHelpers<TData>) => TProps;
}

export type StepConfig<TData = unknown> =
    | SimpleStepConfig<TData>
    | ConditionalStepConfig<TData>
    | FormStepConfig<TData>
    | ComponentStepConfig<any, TData>;

// Helper function to check if a step is using the new type-based configuration
function isTypedStepConfig(step: StepConfig | StepConfig2): step is StepConfig {
    return 'type' in step;
}

// Function to convert new type-based step configs to StepConfig2
function processStepConfig(config: StepConfig | StepConfig2): StepConfig2 {
    if (!isTypedStepConfig(config)) {
        // Already a StepConfig2, return as-is
        return config;
    }

    // Convert type-based config to StepConfig2 using helper functions
    switch (config.type) {
        case 'simple':
            return createSimpleStep(config);
        case 'conditional':
            return createConditionalStep(config);
        case 'form':
            return createFormStep(config);
        case 'component':
            return createComponentStep(config);
        default:
            throw new Error(`Unknown step type: ${(config as any).type}`);
    }
}

interface StepWizard2Props {
    /**
     * Callback when the dialog should be closed
     */
    onClose: () => void;

    /**
     * Array of step configurations (supports both new type-based and legacy StepConfig2)
     */
    steps: (StepConfig | StepConfig2)[];

    /**
     * Optional sidebar/stepper component to render alongside the content. Pass a function to get the
     * wizard's SidebarHelpers, for a sidebar that navigates.
     */
    sidebar?: ReactNode | ((helpers: SidebarHelpers) => ReactNode);

    /**
     * Where the sidebar sits. 'inline' (default) gives it a column beside the step, so the step shifts
     * right by the sidebar's width and the sidebar scrolls with it. 'overlay' pins it under the header
     * at the top-left, out of the layout and outside the scroll area, so the step keeps the centering
     * it has without a sidebar and the sidebar stays in place while the step scrolls. Overlay is shown
     * only while the wizard is at least overlaySidebarMinWidth wide, where the gutter beside a centered
     * step can hold it.
     */
    sidebarPlacement?: 'inline' | 'overlay';

    /**
     * The narrowest wizard width, in px, that shows an overlay sidebar. Measured on the wizard itself, not
     * the viewport, so an open drawer that narrows it hides the sidebar too. Defaults to
     * DEFAULT_OVERLAY_SIDEBAR_MIN_WIDTH, sized for an 800px step.
     */
    overlaySidebarMinWidth?: number;

    /**
     * Optional title for the dialog
     */
    title?: string;

    /**
     * Callback when wizard is completed
     */
    onComplete?: (wizardData: Record<string, any>, stepStates: Record<string, any>) => void | Promise<void>;

    /**
     * Optional loading state
     */
    isLoading?: boolean;

    /**
     * Initial wizard data
     */
    initialWizardData?: Record<string, any>;

    /**
     * Whether to include the IMC component in the header
     */
    includeIMC?: boolean;

    /**
     * Optional slot props for different containers on a general level
     * These props can be overridden by the step config slotProps of that section
     */
    slotProps?: {
        root?: Partial<FullScreenDialogProps>;
        header?: Partial<FullScreenDialogHeaderProps>;
        content?: Partial<FullScreenDialogContentProps>;
        footer?: Partial<FullScreenDialogFooterProps>;
    };

    /**
     * Enable progress persistence in localStorage
     */
    persistProgress?: boolean;

    /**
     * Unique key for this stepper (used for progress persistence and tracking events)
     */
    stepperKey?: string;

    /**
     * Whether to restore last visited step on mount
     */
    restoreOnMount?: boolean;

    /**
     * Clear progress when wizard completes
     */
    clearOnComplete?: boolean;

    /**
     * Enable automatic tracking events for step visits
     */
    sendTrackingEvents?: boolean;

    /**
     * Show footer CTA inline at end of content on mobile instead of sticky footer
     */
    mobileInlineFooter?: boolean;

    /**
     * Always hide the help agent FAB when wizard is active, regardless of footer visibility
     */
    alwaysHideHelpAgentFab?: boolean;
}

export interface StepState {
    canProceed: boolean;
    data: any;
    isLoading?: boolean;
}

// Pre-merged display properties: slide overrides step, resolved once upstream
interface ResolvedStepViewProps {
    title?: string;
    description?: string;
    content: (helpers: StepHelpers) => ReactNode | SlideConfig[];
    footerLeftElement: ReactNode;
    footerRightElement: ReactNode;
}

// The narrowest wizard width where an overlay sidebar (about 240px with its padding) clears an 800px step
// centered in the content area. Below it the sidebar would sit on top of the step, so it is hidden.
const DEFAULT_OVERLAY_SIDEBAR_MIN_WIDTH = 1320;

// Component that lives inside FullScreenDialog provider to access scroll context
interface StepViewProps {
    activeStep: StepConfig2;
    resolvedView: ResolvedStepViewProps;
    currentSlideIndex: number;
    helpers: StepHelpers;
    getConfirmationComponent: () => ReactNode;
    currOpenDrawer: string | null;
    sidebar: ReactNode;
    sidebarPlacement: 'inline' | 'overlay';
    overlaySidebarMinWidth: number;
    title: string;
    includeIMC: boolean;
    mobileInlineFooter: boolean;
    slotProps: StepWizard2Props['slotProps'];
    alwaysHideHelpAgentFab?: boolean;
}

function StepView({
    activeStep,
    resolvedView,
    currentSlideIndex,
    helpers,
    getConfirmationComponent,
    currOpenDrawer,
    sidebar,
    sidebarPlacement,
    overlaySidebarMinWidth,
    title,
    includeIMC,
    mobileInlineFooter,
    slotProps,
    alwaysHideHelpAgentFab,
}: Readonly<StepViewProps>) {
    const location = useLocation();
    const { scrollToTop } = useFullScreenDialog();
    const previousPathnameRef = useRef<string>(location.pathname);
    const { showHelpAgentFab, hideHelpAgentFab } = useIMCContext();

    const hasDefaultFooter = !activeStep.hideDefaultFooter;
    const additionalHeaderHeight = slotProps?.header?.horizontalProgressBarProps ? 18 : 0;

    useEffect(() => {
        if (alwaysHideHelpAgentFab || hasDefaultFooter) {
            hideHelpAgentFab();
        }
        return () => showHelpAgentFab();
    }, [alwaysHideHelpAgentFab, hasDefaultFooter, showHelpAgentFab, hideHelpAgentFab]);

    // Scroll to top when route changes
    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname) {
            scrollToTop();
            previousPathnameRef.current = location.pathname;
        }
    }, [location.pathname, scrollToTop]);

    // Scroll to top when slide changes within a step
    const previousSlideRef = useRef(currentSlideIndex);
    useEffect(() => {
        if (previousSlideRef.current !== currentSlideIndex) {
            scrollToTop();
            previousSlideRef.current = currentSlideIndex;
        }
    }, [currentSlideIndex, scrollToTop]);

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    // Lets the overlay sidebar size against the wizard's own width, which an open drawer narrows.
                    containerType: 'inline-size',
                    ...(currOpenDrawer !== null && {
                        width: {
                            xs: '100dvw',
                            md: `calc(100% - ${RIGHT_DRAWER_WIDTH + 10}px)`,
                        },
                    }),
                }}>
                <FullScreenDialogHeader
                    title={activeStep?.headerTitle || title}
                    rightElement={
                        includeIMC && (
                            <Stack direction="row" spacing={2}>
                                <IMC includePortal={false} disableReferralButton />
                            </Stack>
                        )
                    }
                    leftElement={activeStep.headerLeftElement?.(helpers)}
                    forceDisplayCloseButton={includeIMC}
                    {...slotProps?.header}
                    {...activeStep?.slotProps?.header}
                />
                {sidebar && sidebarPlacement === 'overlay' && (
                    // A zero-height anchor between the header and the scrolling content: the sidebar
                    // hangs from it, so it sits just under the header, stays put while the step
                    // scrolls, and takes no room from the step's layout.
                    <Box
                        sx={{
                            display: 'none',
                            [`@container (min-width: ${overlaySidebarMinWidth}px)`]: { display: 'block' },
                            position: 'relative',
                            height: 0,
                            zIndex: 1,
                        }}>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, pl: 3 }}>{sidebar}</Box>
                    </Box>
                )}
                <FullScreenDialogContent
                    {...slotProps?.content}
                    sx={{
                        height: {
                            xs: `calc(100% - ${
                                (activeStep.hideDefaultFooter || mobileInlineFooter ? 92 : 170) + additionalHeaderHeight
                            }px)`,
                            sm: `calc(100% - ${(activeStep.hideDefaultFooter ? 92 : 170) + additionalHeaderHeight}px)`,
                        },
                        px: 8,
                        scrollbarGutter: 'stable',
                        ...slotProps?.content?.sx,
                    }}>
                    <Stack direction="row" sx={{ height: '100%' }}>
                        {sidebar && sidebarPlacement === 'inline' && (
                            <Box sx={{ display: { xs: 'none', md: 'block' }, pl: 3 }}>{sidebar}</Box>
                        )}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                }}>
                                {resolvedView.title && (
                                    <Typography variant="h1" sx={{ mb: 4, textAlign: 'left' }}>
                                        {resolvedView.title}
                                    </Typography>
                                )}
                                {resolvedView.description && (
                                    <Typography variant="body1" sx={{ mb: 4, textAlign: 'left' }}>
                                        {resolvedView.description}
                                    </Typography>
                                )}
                                {(() => {
                                    const result = resolvedView.content(helpers);
                                    return Array.isArray(result) ? null : result;
                                })()}

                                {/* Inline mobile footer - shown at end of content on mobile */}
                                {!activeStep.hideDefaultFooter && mobileInlineFooter && (
                                    <Box
                                        sx={{
                                            display: { xs: 'flex', sm: 'none' },
                                            flexDirection: 'column',
                                            gap: 2,
                                            mt: 4,
                                            pb: 2,
                                        }}>
                                        {resolvedView.footerRightElement}
                                        {resolvedView.footerLeftElement}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Stack>
                </FullScreenDialogContent>
                {!activeStep.hideDefaultFooter && (
                    <FullScreenDialogFooter
                        leftElement={resolvedView.footerLeftElement}
                        rightElement={resolvedView.footerRightElement}
                        {...slotProps?.footer}
                        sx={{
                            ...(mobileInlineFooter && {
                                display: { xs: 'none', sm: 'flex' },
                            }),
                            ...slotProps?.footer?.sx,
                        }}
                    />
                )}

                {/* Render confirmation modal if configured */}
                {activeStep && getConfirmationComponent()}
            </Box>
            {includeIMC && <RightSidebar instanceId="dialog" />}
        </>
    );
}

export default function StepWizard2({
    onClose,
    steps,
    sidebar = null,
    sidebarPlacement = 'inline',
    overlaySidebarMinWidth = DEFAULT_OVERLAY_SIDEBAR_MIN_WIDTH,
    title = '',
    onComplete = () => {},
    isLoading = false,
    initialWizardData = {},
    includeIMC = false,
    slotProps = {},
    persistProgress = false,
    stepperKey = 'default',
    restoreOnMount = true,
    clearOnComplete = true,
    sendTrackingEvents = false,
    mobileInlineFooter = false,
    alwaysHideHelpAgentFab = false,
}: StepWizard2Props) {
    const location = useLocation();
    const history = useHistory();
    const availablePaths = steps.map((step) => step.path);
    const isAvailablePath = availablePaths.includes(location.pathname);
    const { currOpenDrawer } = useIMCContext();

    // Event tracking hook
    const { sendTrackingEvent } = useEventTracking();

    // Process steps to convert type-based configs to StepConfig2
    const processedSteps: StepConfig2[] = useMemo(() => steps.map(processStepConfig), [steps]);

    // Progress persistence hook
    const { saveLastVisited, getLastVisited, clearProgress } = usePersistStepperProgress({
        wizardKey: stepperKey,
        enabled: persistProgress,
    });

    // Per-step state management
    const [stepStates, setStepStates] = useState<Record<string, StepState>>({});

    // Global wizard state
    const [wizardData, setWizardData] = useState<Record<string, any>>(initialWizardData);

    // Loading state for async operations
    const [isProcessing, setIsProcessing] = useState(false);

    // Confirmation modal state
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const activeStep = processedSteps.find((step) => location.pathname === step.path);
    const currentStepIndex = processedSteps.findIndex((step) => location.pathname === step.path);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === processedSteps.length - 1;

    // Derive current step state early — needed for slide detection below
    const currentStepState = stepStates[activeStep?.path || ''] || {
        canProceed: false,
        data: {},
        isLoading: false,
    };

    // Slide navigation — detection, index management, canProceed resets
    const {
        hasSlides,
        currentSlideIndex,
        isFirstSlide,
        isLastSlide,
        activeSlide,
        totalSlides,
        handleSlideNext,
        handleSlidePrevious,
    } = useSlideNavigation({
        activeStep,
        currentStepState,
        setStepStates,
        wizardData,
        setWizardData,
        currentStepIndex,
        totalSteps: processedSteps.length,
        isFirstStep,
        isLastStep,
    });

    // Single resolution point: slide overrides step for all overlapping display props
    const resolvedPreviousButtonLabel = activeSlide?.previousButtonLabel || activeStep?.previousButtonLabel;
    const resolvedNextButtonLabel = activeSlide?.nextButtonLabel || activeStep?.nextButtonLabel;
    const resolvedFooterLeftFn = activeSlide?.footerLeftElement || activeStep?.footerLeftElement;
    const resolvedFooterRightFn = activeSlide?.footerRightElement || activeStep?.footerRightElement;

    // Initialize step state when step changes (state only, no side effects).
    // Uses functional update to check latest state and prevent race conditions with child components.
    // Note: for slide steps, useSlideNavigation may seed the state first — that's fine,
    // the side effects below fire independently via the enteredStepRef.
    useEffect(() => {
        if (!activeStep) {
            return;
        }

        setStepStates((prev) => {
            if (prev[activeStep.path]) {
                return prev;
            }

            return {
                ...prev,
                [activeStep.path]: {
                    canProceed: activeStep.validateOnMount || false,
                    data: activeStep.initialData || {},
                    isLoading: false,
                },
            };
        });
    }, [activeStep]);

    // Step entry side effects — fires once per step path change, regardless of
    // whether state was created by useSlideNavigation or the initializer above.
    const enteredStepRef = useRef<string>();
    useEffect(() => {
        if (!activeStep || activeStep.path === enteredStepRef.current) {
            return;
        }
        enteredStepRef.current = activeStep.path;

        // Save progress when visiting a step for the first time
        if (persistProgress) {
            const lastVisited = getLastVisited();
            if (!lastVisited || activeStep.path !== lastVisited) {
                const activeStepIndex = processedSteps.findIndex((step) => step.path === activeStep.path);
                const lastVisitedIndex = lastVisited
                    ? processedSteps.findIndex((step) => step.path === lastVisited)
                    : -1;

                if (lastVisitedIndex === -1 || activeStepIndex > lastVisitedIndex) {
                    saveLastVisited(activeStep.path);
                }
            }
        }

        // Call onStepEnter if defined
        if (activeStep.onStepEnter) {
            const stepState = stepStates[activeStep.path];
            activeStep.onStepEnter(stepState?.data ?? activeStep.initialData ?? {}, wizardData);
        }
    }, [activeStep, wizardData, persistProgress, getLastVisited, saveLastVisited, processedSteps, stepStates]);

    // Restore progress on mount
    useEffect(() => {
        // Check for URL parameter to bypass restoration
        const urlParams = new URLSearchParams(location.search);
        const bypassRestore = urlParams.get('bypass-restore') === 'true';

        if (persistProgress && restoreOnMount && !bypassRestore) {
            const lastPath = getLastVisited();
            if (lastPath && lastPath !== location.pathname) {
                const pathExists = processedSteps.some((step) => step.path === lastPath);
                if (pathExists) {
                    history.replace(lastPath);
                }
            }
        }
    }, []); // Only run on mount

    // Navigation functions
    const completeWizard = async () => {
        await onComplete?.(wizardData, stepStates);

        if (persistProgress && clearOnComplete) {
            clearProgress();
        }
    };

    // Use the custom path for conditional steps, otherwise linear progression.
    const advanceTo = (customNextPath?: string) => {
        // `||`, not `??`: an empty path has always meant linear progression.
        const nextPath = customNextPath || processedSteps[currentStepIndex + 1].path;

        if (!processedSteps.some((step) => step.path === nextPath)) {
            sendDatadogRumError('Invalid next path', new Error('Invalid next path'), {
                path: nextPath,
                flowName: stepperKey,
                stepIndex: currentStepIndex,
            });
            return;
        }

        history.push(nextPath);

        // 🔄 SAVE: Only when moving forward to next step
        // This ensures lastVisitedPath represents the furthest point reached,
        // not just the current location (important for back navigation)
        if (persistProgress) {
            saveLastVisited(nextPath);
        }
    };

    const proceedToNext = async (customNextPath?: string, { finish = false }: { finish?: boolean } = {}) => {
        if (!activeStep) {
            return;
        }

        setIsProcessing(true);

        try {
            // Run custom validation if defined
            if (activeStep.customValidation) {
                const isValid = await activeStep.customValidation(currentStepState.data, wizardData);
                if (!isValid) {
                    setIsProcessing(false);
                    return;
                }
            }

            // Call onStepExit if defined
            if (activeStep.onStepExit) {
                const canExit = await activeStep.onStepExit(currentStepState.data, wizardData);
                if (!canExit) {
                    setIsProcessing(false);
                    return;
                }
            }

            if (isLastStep || finish) {
                await completeWizard();
            } else {
                advanceTo(customNextPath);
            }
        } catch (error) {
            sendDatadogRumError(
                'Error during step navigation',
                error instanceof Error ? error : new Error('Error during step navigation'),
                {
                    source: 'StepWizard2',
                    action: 'handleNext',
                    context: 'step_navigation',
                    currentPath: location.pathname,
                }
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleNext = async () => {
        if (!activeStep || !currentStepState.canProceed) {
            return;
        }

        if (handleSlideNext()) {
            return;
        }

        // Track CTA button click when enabled
        if (sendTrackingEvents) {
            const displayFlowName = getDisplayFlowName(stepperKey);
            sendTrackingEvent(`${displayFlowName} Continued`, {
                path: activeStep.path,
                flowName: stepperKey,
                buttonLabel: isLastStep
                    ? activeStep.submitButtonLabel || 'Complete'
                    : activeStep.nextButtonLabel || 'Next',
                stepIndex: currentStepIndex,
                isLastStep,
            });
        }

        // Check if confirmation is needed
        const stepConfig = steps.find((step) => step.path === activeStep.path);
        if (stepConfig?.confirmation) {
            const shouldShowConfirmation = stepConfig.confirmation.condition
                ? stepConfig.confirmation.condition(currentStepState.data)
                : true;

            if (shouldShowConfirmation) {
                setIsConfirmationOpen(true);
                return;
            }
        }

        // Determine custom next path in priority order:
        // 1. getNextPath function (for conditional steps)
        // 2. explicit nextPath property
        let customNextPath: string | undefined;
        if (stepConfig && 'getNextPath' in stepConfig && typeof stepConfig.getNextPath === 'function') {
            customNextPath = stepConfig.getNextPath(currentStepState.data);
        } else if (activeStep?.nextPath) {
            customNextPath = activeStep.nextPath;
        }

        await proceedToNext(customNextPath);
    };

    // One event for every way back (footer, sidebar), so their analytics cannot drift apart.
    const trackBackClick = (targetPath: string, source?: 'sidebar') => {
        if (!sendTrackingEvents || !activeStep) {
            return;
        }
        sendTrackingEvent(`${getDisplayFlowName(stepperKey)} Back Button Clicked`, {
            path: activeStep.path,
            flowName: stepperKey,
            previousPath: targetPath,
            stepIndex: currentStepIndex,
            ...(source && { source }),
        });
    };

    // No confirmation and no slides: both belong to advancing through the step, and finishing skips
    // past everything after it.
    const handleFinish = async () => {
        if (!activeStep || !currentStepState.canProceed) {
            return;
        }

        if (sendTrackingEvents) {
            sendTrackingEvent(`${getDisplayFlowName(stepperKey)} Continued`, {
                path: activeStep.path,
                flowName: stepperKey,
                buttonLabel: activeStep.submitButtonLabel || 'Complete',
                stepIndex: currentStepIndex,
                isLastStep: true,
            });
        }

        await proceedToNext(undefined, { finish: true });
    };

    const handlePrevious = () => {
        if (handleSlidePrevious()) {
            return;
        }

        if (currentStepIndex > 0) {
            // Use explicit prevPath if defined, otherwise use array-based navigation
            const targetPath = activeStep?.prevPath || processedSteps[currentStepIndex - 1].path;

            trackBackClick(targetPath);
            history.push(targetPath);
        }
    };

    const handleConfirmationConfirm = async () => {
        setIsConfirmationOpen(false);

        const stepConfig = steps.find((step) => step.path === activeStep?.path);
        if (stepConfig?.confirmation?.onConfirm) {
            await stepConfig.confirmation.onConfirm();
        } else {
            await proceedToNext();
        }
    };

    const handleConfirmationCancel = () => {
        setIsConfirmationOpen(false);
    };

    const handleSkipToReview = async (targetPath: string) => {
        if (!activeStep || !currentStepState.canProceed) {
            return;
        }

        setIsProcessing(true);
        try {
            if (activeStep.onStepExit) {
                const canExit = await activeStep.onStepExit(currentStepState.data, wizardData);
                if (!canExit) {
                    return;
                }
            }

            history.push(targetPath);
            if (persistProgress) {
                saveLastVisited(targetPath);
            }
        } catch (error) {
            sendDatadogRumError(
                'Skip navigation error',
                error instanceof Error ? error : new Error('Skip navigation error'),
                {
                    source: 'StepWizard2',
                    action: 'handleSkipToReview',
                }
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSubmit = async (data: any) => {
        if (!activeStep) {
            return;
        }

        // Update step data
        setStepStates((prev) => ({
            ...prev,
            [activeStep.path]: { ...prev[activeStep.path], data },
        }));

        // Proceed to next step or complete
        await handleNext();
    };

    // Helper functions factory
    const createStepHelpers = (step: StepConfig2): StepHelpers => ({
        setCanProceed: (canProceed: boolean) => {
            setStepStates((prev) => ({
                ...prev,
                [step.path]: { ...prev[step.path], canProceed },
            }));
        },

        canProceed: currentStepState.canProceed,

        setStepData: (data: any) => {
            setStepStates((prev) => ({
                ...prev,
                [step.path]: { ...prev[step.path], data },
            }));
        },

        getStepData: () => currentStepState.data,

        onNext: handleNext,
        onFinish: handleFinish,
        onPrevious: handlePrevious,
        onSubmit: handleSubmit,
        goToStep: (stepNumber: number) => {
            const targetIndex = stepNumber - 1;

            if (targetIndex >= 0 && targetIndex < processedSteps.length) {
                const targetStep = processedSteps[targetIndex];
                history.push(targetStep.path);
            }
        },

        setWizardData: (key: string, value: any) => {
            setWizardData((prev) => ({ ...prev, [key]: value }));
        },

        getWizardData: (key: string) => wizardData[key],
        getAllWizardData: () => wizardData,

        currentStepIndex,
        totalSteps: processedSteps.length,
        isFirstStep,
        isLastStep,

        // Slide context
        currentSlideIndex: hasSlides ? currentSlideIndex : undefined,
        totalSlides,
        isFirstSlide: hasSlides ? isFirstSlide : undefined,
        isLastSlide: hasSlides ? isLastSlide : undefined,
    });

    if (!isAvailablePath) {
        return <Redirect to="/not-found" />;
    }

    if (!processedSteps.length || !activeStep) {
        return null;
    }

    const helpers = createStepHelpers(activeStep);

    const footerHelpers: StepFooterHelpers = {
        ...helpers,
        canProceed: currentStepState.canProceed,
        stepData: currentStepState.data,
        isLoading: isProcessing || isLoading || currentStepState.isLoading,
        setIsLoading: setIsProcessing,
    };

    // Default footer elements
    const isButtonLoading = isProcessing || isLoading || currentStepState.isLoading;

    // Same guard and tracking as the footer's Back, so a sidebar click is no way around either.
    const goBackTo = (targetPath: string) => {
        // Earlier steps only: going forward this way would skip the active step's validation and exit checks.
        const targetIndex = processedSteps.findIndex((step) => step.path === targetPath);
        if (isButtonLoading || !activeStep || targetIndex < 0 || targetIndex >= currentStepIndex) {
            return;
        }
        trackBackClick(targetPath, 'sidebar');
        history.push(targetPath);
    };
    const resolvedSidebar =
        typeof sidebar === 'function' ? sidebar({ isProcessing: Boolean(isButtonLoading), goBackTo }) : sidebar;

    // Show back button: on non-first steps, or on non-first slides within a multi-slide step
    const showBackButton = hasSlides ? !isFirstSlide || !isFirstStep : !isFirstStep;
    const backButtonLabel = resolvedPreviousButtonLabel || 'Back';

    const defaultLeftElement = showBackButton ? (
        <Button
            variant="text"
            color="secondary"
            startIcon={<ChevronLeft />}
            {...activeStep.previousButtonProps}
            onClick={handlePrevious}
            disabled={isButtonLoading || activeStep.previousButtonProps?.disabled}
            sx={{
                display: { xs: 'none', sm: 'flex' },
                ...activeStep.previousButtonProps?.sx,
            }}>
            {backButtonLabel}
        </Button>
    ) : null;

    const getNextButtonLabel = () => {
        if (isButtonLoading) {
            return 'Loading...';
        }
        // Submit label only on last step AND last slide (or no slides)
        if (isLastStep && (!hasSlides || isLastSlide)) {
            return activeStep.submitButtonLabel || 'Complete';
        }
        return resolvedNextButtonLabel || 'Next';
    };

    const { skipToReviewPath } = activeStep;
    const defaultRightElement = skipToReviewPath ? (
        <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={2}
            sx={{ width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}>
            <Button
                variant="text"
                color="secondary"
                onClick={() => handleSkipToReview(skipToReviewPath)}
                disabled={!currentStepState.canProceed || isButtonLoading}
                sx={{ display: { xs: 'flex', sm: 'none' } }}>
                Skip to review
            </Button>
            <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleSkipToReview(skipToReviewPath)}
                disabled={!currentStepState.canProceed || isButtonLoading}
                sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: '140px' }}>
                Skip to review
            </Button>
            <Button
                variant="contained"
                onClick={handleNext}
                disabled={!currentStepState.canProceed || isButtonLoading}
                sx={{ minWidth: { xs: '100%', sm: '120px' } }}>
                {getNextButtonLabel()}
            </Button>
        </Stack>
    ) : (
        <Button
            variant="contained"
            sx={{
                minWidth: { xs: '100%', sm: '140px' },
                marginLeft: { xs: '0 !important', sm: 'auto' },
                display: activeStep.hideNextButton ? 'none' : 'flex',
            }}
            {...activeStep.nextButtonProps}
            onClick={handleNext}
            disabled={!currentStepState.canProceed || isButtonLoading}>
            {getNextButtonLabel()}
        </Button>
    );

    const getConfirmationComponent = () => {
        if (!activeStep || !isConfirmationOpen || !activeStep.confirmation) {
            return null;
        }
        const ConfirmationComponent = activeStep.confirmation.component;
        return (
            <ConfirmationComponent
                isOpen={isConfirmationOpen}
                onConfirm={handleConfirmationConfirm}
                onCancel={handleConfirmationCancel}
                stepData={currentStepState.data}
            />
        );
    };

    // Fully resolved view: slide overrides step, footer fns called with defaults applied
    const resolvedView: ResolvedStepViewProps = {
        title: activeSlide?.title ?? activeStep.title,
        description: activeSlide?.description ?? activeStep.description,
        content: activeSlide ? activeSlide.content : activeStep.content,
        footerLeftElement: resolvedFooterLeftFn?.(footerHelpers) || defaultLeftElement,
        footerRightElement: resolvedFooterRightFn?.(footerHelpers) || defaultRightElement,
    };

    const paperProps = {
        ...slotProps.root?.paperProps,
        ...activeStep?.slotProps?.root?.paperProps,
        sx: {
            flexDirection: 'row',
            ...(slotProps.root?.paperProps?.sx || {}),
            ...(activeStep?.slotProps?.root?.paperProps?.sx || {}),
        },
    } as Omit<PaperProps, 'ref'>;

    return (
        <FullScreenDialog
            open
            onClose={onClose}
            disableEscapeKeyDown
            {...slotProps.root}
            {...activeStep?.slotProps?.root}
            paperProps={paperProps}>
            <StepView
                activeStep={activeStep}
                resolvedView={resolvedView}
                currentSlideIndex={currentSlideIndex}
                helpers={helpers}
                getConfirmationComponent={getConfirmationComponent}
                currOpenDrawer={currOpenDrawer}
                sidebar={resolvedSidebar}
                sidebarPlacement={sidebarPlacement}
                overlaySidebarMinWidth={overlaySidebarMinWidth}
                title={title}
                includeIMC={includeIMC}
                mobileInlineFooter={mobileInlineFooter}
                slotProps={slotProps}
                alwaysHideHelpAgentFab={alwaysHideHelpAgentFab}
            />
        </FullScreenDialog>
    );
}
